import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Stack,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridSortModel,
} from '@mui/x-data-grid';
import {
  Block,
  CheckCircle,
  Visibility,
  Download,
  Search,
  Refresh,
  Close,
  Smartphone,
  LocationOn,
  Wifi,
  Security as SecurityIcon,
  BarChart,
  Clear,
  VerifiedUser,
  Science,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { Device, FilterOptions, SortOptions } from '../../types';
import { format } from 'date-fns';
import ExportDialog from '../../components/Export/ExportDialog';

type DetailTab = 'info' | 'location' | 'network' | 'usage' | 'security';

const fmtDate = (v?: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : format(d, 'MMM dd, yyyy HH:mm');
};

const fmtBytes = (mb?: number | null) => (typeof mb === 'number' && mb > 0 ? `${mb} MB` : '—');

/** One label/value line in the detail modal. */
const Row: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, alignItems: 'baseline' }}>
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
    <Typography
      variant="body2"
      sx={{ textAlign: 'right', wordBreak: 'break-all', fontFamily: mono ? 'monospace' : undefined, fontSize: mono ? 12 : undefined }}
    >
      {value ?? '—'}
    </Typography>
  </Box>
);

const DevicesList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // The URL is the source of truth for search, so other pages can deep-link
  // straight to a device (e.g. NSFW monitoring → /admin/devices?deviceId=xyz).
  const urlSearch = searchParams.get('deviceId') || searchParams.get('search') || '';

  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25, total: 0 });
  const [filters, setFilters] = useState<FilterOptions>({});
  const [sort, setSort] = useState<SortOptions>({ field: 'createdAt', order: 'desc' });
  const [searchInput, setSearchInput] = useState(urlSearch);

  // Detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [activeTab, setActiveTab] = useState<DetailTab>('info');
  const [actionBusy, setActionBusy] = useState(false);

  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [blockTarget, setBlockTarget] = useState<{ deviceId: string } | null>(null);

  // External URL change (deep link) → sync the box and reset to page 1.
  useEffect(() => {
    setSearchInput(urlSearch);
    setPagination((p) => ({ ...p, page: 0 }));
  }, [urlSearch]);

  // Typing → URL (debounced). Fetching keys off the URL, so this drives the query.
  useEffect(() => {
    const t = setTimeout(() => {
      const v = searchInput.trim();
      if (v === urlSearch) return;
      setSearchParams(v ? { search: v } : {}, { replace: true });
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput, urlSearch, setSearchParams]);

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getDevices(
        pagination.page + 1,
        pagination.pageSize,
        { ...filters, ...(urlSearch ? { search: urlSearch } : {}) },
        sort
      );

      if (response.success && response.data) {
        setDevices(response.data.documents || []);
        setPagination((prev) => ({ ...prev, total: response.data!.pagination.totalCount }));
      } else {
        setError(response.error || 'Failed to fetch devices');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch devices');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters, sort, urlSearch]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const openDetail = async (deviceId: string) => {
    setDetailOpen(true);
    setActiveTab('info');
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const res = await apiService.getDevice(deviceId);
      if (res.success && res.data) setDetail(res.data);
      else setDetailError(res.error || 'Failed to load device');
    } catch (err: any) {
      setDetailError(err?.response?.data?.error || err.message || 'Failed to load device');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (deviceId: string) => {
    try {
      const res = await apiService.getDevice(deviceId);
      if (res.success && res.data) setDetail(res.data);
    } catch {
      /* keep the current view */
    }
  };

  const handleUnblock = async (deviceId: string) => {
    setActionBusy(true);
    try {
      const res = await apiService.unblockDevice(deviceId);
      if (res.success) {
        await fetchDevices();
        if (detailOpen) await refreshDetail(deviceId);
      } else setError(res.error || 'Failed to unblock device');
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to unblock device');
    } finally {
      setActionBusy(false);
    }
  };

  const handleBlockConfirm = async () => {
    if (!blockTarget) return;
    setActionBusy(true);
    try {
      const res = await apiService.blockDevice(blockTarget.deviceId, blockReason);
      if (res.success) {
        setBlockDialogOpen(false);
        setBlockReason('');
        await fetchDevices();
        if (detailOpen) await refreshDetail(blockTarget.deviceId);
        setBlockTarget(null);
      } else setError(res.error || 'Failed to block device');
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to block device');
    } finally {
      setActionBusy(false);
    }
  };

  const handleToggleBypass = async (deviceId: string, currentlyBypassed: boolean) => {
    setActionBusy(true);
    try {
      if (currentlyBypassed) await apiService.unbypassNsfwOffender({ deviceId });
      else await apiService.bypassNsfwOffender({ deviceId, reason: 'Admin NSFW bypass' });
      await refreshDetail(deviceId);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to update NSFW bypass');
    } finally {
      setActionBusy(false);
    }
  };

  const handleToggleForceVision = async (deviceId: string, currentlyForced: boolean) => {
    setActionBusy(true);
    try {
      if (currentlyForced) await apiService.unforceVisionNsfwOffender({ deviceId });
      else await apiService.forceVisionNsfwOffender({ deviceId, reason: 'Admin forced Vision scan' });
      await refreshDetail(deviceId);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to update forced Vision');
    } finally {
      setActionBusy(false);
    }
  };

  const handleFilterChange = (field: keyof FilterOptions, value: any) => {
    setFilters((prev) => ({ ...prev, [field]: value || undefined }));
    setPagination((p) => ({ ...p, page: 0 }));
  };

  // Server-side sorting — the grid only holds one page, so sorting must round-trip.
  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setSort({ field: model[0].field, order: model[0].sort || 'asc' });
    } else {
      setSort({ field: 'createdAt', order: 'desc' });
    }
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const columns: GridColDef[] = [
    {
      field: 'deviceId',
      headerName: 'Device ID',
      width: 190,
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap>
            {params.value ? `${String(params.value).substring(0, 12)}…` : '—'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'platform',
      headerName: 'Platform',
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <Chip
            label={params.value}
            size="small"
            color={params.value === 'android' ? 'success' : params.value === 'ios' ? 'info' : 'default'}
          />
        ) : ('—'),
    },
    { field: 'deviceModel', headerName: 'Model', width: 150 },
    { field: 'deviceBrand', headerName: 'Brand', width: 110 },
    { field: 'osVersion', headerName: 'OS', width: 90 },
    { field: 'appVersion', headerName: 'App', width: 90 },
    { field: 'country', headerName: 'Country', width: 120 },
    { field: 'totalFaceSwaps', headerName: 'Swaps', width: 90, type: 'number' },
    {
      field: 'isBlocked',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => (
        <Chip label={params.value ? 'Blocked' : 'Active'} size="small" color={params.value ? 'error' : 'success'} />
      ),
    },
    {
      field: 'suspiciousActivity',
      headerName: 'Suspicious',
      width: 110,
      renderCell: (params) => (
        <Chip label={params.value ?? 0} size="small" color={(params.value ?? 0) > 0 ? 'warning' : 'default'} />
      ),
    },
    {
      field: 'lastActive',
      headerName: 'Last Active',
      width: 160,
      renderCell: (params) =>
        params.value ? (
          <Typography variant="body2">{fmtDate(params.value)}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">Never</Typography>
        ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 90,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Visibility />}
          label="View Details"
          onClick={() => openDetail(params.row.deviceId)}
        />,
        <GridActionsCellItem
          icon={params.row.isBlocked ? <CheckCircle /> : <Block />}
          label={params.row.isBlocked ? 'Unblock' : 'Block'}
          onClick={() => {
            if (params.row.isBlocked) handleUnblock(params.row.deviceId);
            else {
              setBlockTarget({ deviceId: params.row.deviceId });
              setBlockDialogOpen(true);
            }
          }}
        />,
      ],
    },
  ];

  const info = detail?.deviceInfo || {};
  const loc = detail?.location || {};
  const net = detail?.network || {};
  const usage = detail?.appUsage || {};
  const sec = detail?.security || {};
  const ipGeo = detail?.ipGeo || {};
  const isBypassed = sec?.nsfwBypassed === true;
  const isForceVision = sec?.nsfwForceVision === true;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Devices</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Download />} onClick={() => setExportDialogOpen(true)}>
            Export
          </Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchDevices}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Search"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Device ID, model, brand, country, IP…"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchInput('')}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <TextField
            select
            label="Platform"
            size="small"
            value={filters.platform || ''}
            onChange={(e) => handleFilterChange('platform', e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="android">Android</MenuItem>
            <MenuItem value="ios">iOS</MenuItem>
            <MenuItem value="web">Web</MenuItem>
            <MenuItem value="desktop">Desktop</MenuItem>
          </TextField>
          <TextField
            select
            label="Status"
            size="small"
            value={filters.isBlocked === undefined ? '' : String(filters.isBlocked)}
            onChange={(e) =>
              handleFilterChange('isBlocked', e.target.value === '' ? undefined : e.target.value === 'true')
            }
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="false">Active</MenuItem>
            <MenuItem value="true">Blocked</MenuItem>
          </TextField>
          <TextField
            label="Country"
            size="small"
            value={filters.country || ''}
            onChange={(e) => handleFilterChange('country', e.target.value)}
          />
          {urlSearch && (
            <Chip
              label={`Filtered: ${urlSearch.length > 24 ? `${urlSearch.slice(0, 24)}…` : urlSearch}`}
              onDelete={() => setSearchInput('')}
              color="primary"
              size="small"
            />
          )}
        </Box>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ height: 620, width: '100%' }}>
        <DataGrid
          rows={devices || []}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
          rowCount={pagination.total}
          onPaginationModelChange={(model) =>
            setPagination((prev) => ({ ...prev, page: model.page, pageSize: model.pageSize }))
          }
          onSortModelChange={handleSortModelChange}
          onRowClick={(params) => openDetail(params.row.deviceId)}
          disableRowSelectionOnClick
          disableColumnMenu
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Paper>

      {/* Device detail modal */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <Smartphone color="primary" />
          <span>Device detail</span>
          {detail && (
            <Chip
              label={sec?.isBlocked ? 'Blocked' : 'Active'}
              size="small"
              color={sec?.isBlocked ? 'error' : 'success'}
            />
          )}
          {isBypassed && (
            <Chip
              icon={<VerifiedUser />}
              label="NSFW bypassed"
              size="small"
              sx={{ bgcolor: '#f97316', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
            />
          )}
          {(ipGeo?.isGoogleTestDevice || ipGeo?.isDatacenterIp || info?.isEmulator) && (
            <Chip
              icon={<Science />}
              label="Reviewer"
              size="small"
              sx={{ bgcolor: '#f59e0b', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
            />
          )}
          <IconButton onClick={() => setDetailOpen(false)} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>
          ) : detailError ? (
            <Alert severity="error">{detailError}</Alert>
          ) : !detail ? null : (
            <>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }} variant="scrollable">
                <Tab value="info" icon={<Smartphone fontSize="small" />} iconPosition="start" label="Info" />
                <Tab value="location" icon={<LocationOn fontSize="small" />} iconPosition="start" label="Location" />
                <Tab value="network" icon={<Wifi fontSize="small" />} iconPosition="start" label="Network" />
                <Tab value="usage" icon={<BarChart fontSize="small" />} iconPosition="start" label="Usage" />
                <Tab value="security" icon={<SecurityIcon fontSize="small" />} iconPosition="start" label="Security" />
              </Tabs>

              {activeTab === 'info' && (
                <Stack divider={<Divider flexItem />}>
                  <Row label="Device ID" value={detail.deviceId} mono />
                  <Row label="Platform" value={info.platform} />
                  <Row label="Brand / Model" value={[info.deviceBrand, info.deviceModel].filter(Boolean).join(' ') || '—'} />
                  <Row label="Device name" value={info.deviceName} />
                  <Row label="OS version" value={info.osVersion} />
                  <Row label="App version" value={[info.appVersion, info.buildNumber && `(${info.buildNumber})`].filter(Boolean).join(' ') || '—'} />
                  <Row label="Screen" value={[info.screenResolution, info.screenDensity && `@${info.screenDensity}x`].filter(Boolean).join(' ') || '—'} />
                  <Row label="Memory" value={fmtBytes(info.totalMemory)} />
                  <Row label="Storage" value={fmtBytes(info.storageTotal)} />
                  <Row label="CPU" value={info.cpuArchitecture} />
                  <Row label="Build fingerprint" value={info.fingerprint} mono />
                  <Row label="First seen" value={fmtDate(detail.createdAt)} />
                </Stack>
              )}

              {activeTab === 'location' && (
                <Stack divider={<Divider flexItem />}>
                  <Row label="Country" value={[loc.country, loc.countryCode && `(${loc.countryCode})`].filter(Boolean).join(' ') || '—'} />
                  <Row label="Region" value={loc.region} />
                  <Row label="City" value={loc.city} />
                  <Row label="Timezone" value={loc.timezone} />
                  <Row label="Locale / Language" value={[loc.locale, loc.language].filter(Boolean).join(' · ') || '—'} />
                  <Row label="Currency" value={loc.currency} />
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                    Resolved from IP
                  </Typography>
                  <Row label="Country" value={ipGeo.country} />
                  <Row label="Region" value={ipGeo.regionName || ipGeo.region} />
                  <Row label="City" value={ipGeo.city} />
                  <Row label="Timezone" value={ipGeo.timezone} />
                  <Row label="Resolved at" value={fmtDate(ipGeo.lastUpdatedAt)} />
                </Stack>
              )}

              {activeTab === 'network' && (
                <Stack divider={<Divider flexItem />}>
                  <Row label="IP address" value={ipGeo.ip || net.ipAddress} mono />
                  <Row label="Connection" value={net.connectionType} />
                  <Row label="Carrier" value={net.carrier} />
                  <Row label="ISP" value={ipGeo.isp} />
                  <Row label="Organisation" value={ipGeo.org} />
                  <Row label="ASN" value={ipGeo.as} />
                  <Row label="Reverse DNS" value={ipGeo.reverse} mono />
                  <Row label="Hosting / datacenter" value={ipGeo.hosting ? 'Yes' : 'No'} />
                  <Row label="Proxy / VPN" value={ipGeo.proxy ? 'Yes' : 'No'} />
                  <Row label="Mobile network" value={ipGeo.mobile ? 'Yes' : 'No'} />
                  <Row label="Source" value={ipGeo.source} />
                </Stack>
              )}

              {activeTab === 'usage' && (
                <Stack divider={<Divider flexItem />}>
                  <Row label="Total face swaps" value={usage.totalFaceSwaps ?? 0} />
                  <Row label="Image swaps" value={usage.imageFaceSwaps ?? 0} />
                  <Row label="Video swaps" value={usage.videoFaceSwaps ?? 0} />
                  <Row label="Total sessions" value={usage.totalSessions ?? 0} />
                  <Row label="First launch" value={fmtDate(usage.firstLaunch)} />
                  <Row label="Last active" value={fmtDate(usage.lastActive)} />
                </Stack>
              )}

              {activeTab === 'security' && (
                <Box>
                  <Stack divider={<Divider flexItem />}>
                    <Row label="Blocked" value={sec.isBlocked ? 'Yes' : 'No'} />
                    {sec.isBlocked && <Row label="Block reason" value={sec.blockReason} />}
                    <Row label="Suspicious activity" value={sec.suspiciousActivity ?? 0} />
                    <Row label="Emulator" value={info.isEmulator ? 'Yes' : 'No'} />
                    <Row label="Rooted" value={info.isRooted ? 'Yes' : 'No'} />
                    <Row label="NSFW bypass" value={isBypassed ? 'Enabled' : 'Disabled'} />
                    {isBypassed && <Row label="Bypass reason" value={sec.nsfwBypassReason} />}
                    {isBypassed && <Row label="Bypassed at" value={fmtDate(sec.nsfwBypassAt)} />}
                    <Row label="Force Vision scan" value={isForceVision ? 'Enabled' : 'Disabled'} />
                    {isForceVision && <Row label="Forced at" value={fmtDate(sec.nsfwForceVisionAt)} />}
                  </Stack>

                  {/* Signals that force NSFW moderation on regardless of settings */}
                  {(ipGeo.isGoogleTestDevice || ipGeo.isDatacenterIp || info.isEmulator) && (
                    <Alert severity="warning" icon={<Science fontSize="small" />} sx={{ mt: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                        Reviewer / test device — NSFW check forced on
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                        Always scanned, even while the global check is off. A bypass does not apply.
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {[
                          ...(ipGeo.isGoogleTestDevice ? (ipGeo.googleTestReasons || ['Google-owned IP']) : []),
                          ...(ipGeo.isDatacenterIp ? (ipGeo.datacenterReasons || ['Datacenter IP']) : []),
                          ...(info.isEmulator ? ['Client reports running on an emulator'] : []),
                        ].map((r: string, i: number) => (
                          <Typography component="li" variant="caption" key={i}>{r}</Typography>
                        ))}
                      </Box>
                    </Alert>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Actions
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {sec.isBlocked ? (
                      <Button
                        size="small"
                        color="success"
                        variant="outlined"
                        startIcon={<CheckCircle />}
                        disabled={actionBusy}
                        onClick={() => handleUnblock(detail.deviceId)}
                      >
                        Unblock device
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        startIcon={<Block />}
                        disabled={actionBusy}
                        onClick={() => {
                          setBlockTarget({ deviceId: detail.deviceId });
                          setBlockDialogOpen(true);
                        }}
                      >
                        Block device
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VerifiedUser />}
                      disabled={actionBusy}
                      sx={{ color: '#c2410c', borderColor: '#f97316' }}
                      onClick={() => handleToggleBypass(detail.deviceId, isBypassed)}
                    >
                      {isBypassed ? 'Remove NSFW bypass' : 'Bypass NSFW'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Science />}
                      disabled={actionBusy}
                      sx={{ color: '#1d4ed8', borderColor: '#4285f4' }}
                      onClick={() => handleToggleForceVision(detail.deviceId, isForceVision)}
                    >
                      {isForceVision ? 'Remove forced Vision' : 'Force Vision scan'}
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Force Vision always scans this device with Google Vision, overriding the global
                    engine and any bypass. To force a specific user across all their devices, use
                    "Force Vision scan" from an NSFW incident instead.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Block Device Dialog */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Block Device</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {blockTarget?.deviceId}
          </Typography>
          <TextField
            fullWidth
            label="Reason for blocking"
            multiline
            rows={3}
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBlockConfirm} variant="contained" color="error" disabled={actionBusy}>
            Block Device
          </Button>
        </DialogActions>
      </Dialog>

      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        dataType="devices"
        data={devices}
        title="Export Devices"
      />
    </Box>
  );
};

export default DevicesList;
